/**
 * `/app/me/apps` — the AI clients that can reach Boone on this person's behalf, and the lever
 * that cuts one off (issue #116, ADR 0021 decision 9).
 *
 * **There is no parameter for whose apps**, on either call. The account is the session's own,
 * decided server-side from the verified `sub`, and that absence is the authorization boundary
 * rather than an omission — the same shape as `leave_family` taking no target. The only id
 * that crosses names an **app**, and the server refuses one that is not in the caller's own
 * listing before it asks Stytch anything.
 *
 * **Every sentence here is the server's.** The question in the confirmation, what cutting off
 * achieves, and the window it cannot close all arrive composed. The last is why: the residual
 * is a measured property of the Stytch project (ADR 0021 decision 9), and a browser bundle
 * carrying its own *"eight hours"* would go on saying eight the day the project changed.
 *
 * **`logo_url` is not on the wire and this file has no field for it.** Under Dynamic Client
 * Registration it is an attacker-controllable URL, and #112's CSP on this host would refuse to
 * load it anyway — decision 9 drops it from what the surface promises rather than shipping a
 * field the page cannot render.
 */

import { type AppApi, appFetch } from "./client";

/** The three sentences the confirmation dialog renders, composed server-side. */
export interface CutOffConfirmation {
  readonly message: string;
  readonly consequence: string;
  /** ADR 0021 decision 9's residual — *"it may keep working for up to eight hours"*. */
  readonly caution: string;
}

/**
 * One row on the screen.
 *
 * A union rather than a `state` beside optional fields, because the two rows genuinely carry
 * different things: a live grant has a confirmation to show and no expiry yet, and a
 * cut-off one has an expiry and nothing left to confirm. Rendering code that had to check
 * both would be able to forget.
 */
export type ConnectedApp =
  | {
      readonly connectedAppId: string;
      /** What the app calls itself. A claim, never an attestation — see {@link ConnectedApps}. */
      readonly claimedName: string;
      readonly claimedDescription: string | null;
      /** What a grant lets it do, in the family's terms. Never the raw OIDC scope string. */
      readonly can: readonly string[];
      readonly state: "connected";
      readonly residualMinutes: number;
      readonly confirm: CutOffConfirmation;
    }
  | {
      readonly connectedAppId: string;
      readonly claimedName: string;
      readonly state: "winding_down";
      /** ISO-8601 upper bound on when its last pass stops working. */
      readonly endsAt: string;
    };

/**
 * What the screen renders itself from — **without drawing a single app**.
 *
 * The rows still arrive; the page no longer lists them. Under Dynamic Client Registration the
 * only thing we are told about a grant is the name the app typed for itself, and the listing
 * carries no first-seen, no last-used and no device — so four cards reading *Calls itself
 * "Claude"* are genuinely indistinguishable, and asking somebody to pick one is asking them to
 * aim a decision they have no way to aim. A count and one blunt lever is the honest surface.
 *
 * Every sentence and every number here is composed server-side, including the window: a
 * browser bundle deriving it would be a second place it is decided.
 */
export interface ConnectedAppsSummary {
  readonly connectedCount: number;
  readonly windingDownCount: number;
  /** ISO-8601 — the **latest** instant a cut-off app stops working, rendered in the reader's zone. */
  readonly windingDownUntil: string | null;
  readonly nothingConnected: string;
  /** `null` when nothing is connected, which is what greys the button off. */
  readonly confirmAll: CutOffConfirmation | null;
}

export interface ConnectedApps {
  readonly apps: readonly ConnectedApp[];
  readonly summary: ConnectedAppsSummary;
  /**
   * What a winding-down row says about itself, once. One sentence about the mechanism, true
   * of every cut-off app and of none in particular — so it rides at the top level rather than
   * being repeated per row, and it comes from the server rather than from here.
   */
  readonly windingDownNote: string;
}

export function fetchConnectedApps(api: AppApi): Promise<ConnectedApps> {
  return appFetch<ConnectedApps>(api, "/app/me/apps");
}

/** What a completed cut-off-everything answers with. */
export interface CutOffAll {
  readonly requested: number;
  readonly cutOff: number;
  /** How many survived. Non-zero means *press it again*, never *which ones*. */
  readonly failed: number;
  /** ISO-8601 — when the last of them stops working. `null` when there was nothing to do. */
  readonly endsAt: string | null;
  readonly residualMinutes: number;
}

/**
 * `POST /app/me/apps/revoke-all` — cut every app off.
 *
 * **Nothing that names an app.** The body is empty (`{}`, which is what `appFetch` needs to
 * send a POST at all): there is no id to give, because the set is the session's own grants,
 * decided server-side. That absence is the authorization boundary, the same as the listing's.
 */
export function cutOffAllApps(api: AppApi): Promise<CutOffAll> {
  return appFetch<CutOffAll>(api, "/app/me/apps/revoke-all", { method: "POST", body: {} });
}
